import React from 'react';
import { Button } from '../Utility/Button';
import { GlobeIcon, GlobeXIcon, PencilSimpleIcon, TrashIcon } from '@phosphor-icons/react';
import NewChat from '../Utility/Icons/NewChat';
import { useNavigate } from 'react-router-dom';
import Avatar from './Avatar';
import AdminBox from '../Utility/AdminBox';
import { Prompt } from '../../Types';
import { ChatMessageRole } from '../../Types/Enums';
import { RootState, useAppSelector } from '../../store';
import { UserSettings } from '../../Types';

interface UserPromptCardProps {
    prompt: Prompt;
    createNewChat: (identifier: string) => void;
    handleRemoveProposedGlobal: (promptIdentifier: string) => void;
    handleProposeGlobal: (promptIdentifier: string) => void;
    handleDelete: (promptIdentifier: string) => void;
    handleEdit: (identifier: string) => void;
}

export default function UserPromptCard({
    prompt,
    createNewChat,
    handleRemoveProposedGlobal,
    handleProposeGlobal,
    handleDelete,
    handleEdit,
}: UserPromptCardProps) {
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const navigate = useNavigate();
    const userSettings = useAppSelector((state: RootState) => state.userSettings as UserSettings);
    return (
        <AdminBox classString="group flex flex-col gap-5 mb-0 col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
            <div
                className="flex flex-col gap-5 cursor-pointer w-full flex-grow"
                onClick={() => createNewChat(prompt.identifier)}
            >
                <div className="flex gap-4">
                    <div className=" w-[50px] h-[50px]">
                        <div className="relative w-[50px] h-[50px]">
                            <Avatar role={ChatMessageRole.assistant} title={prompt.name} avatarImage={prompt.image} />
                        </div>
                    </div>
                    <div className="flex flex-col justify-center">
                        <h6 className="max-w-full p-0 line-clamp-2">{prompt.name}</h6>
                    </div>
                </div>
                <div>
                    <p className="max-h-[60px] text-layout-textColor m-0 p-0 line-clamp-3">{prompt.description}</p>
                </div>
            </div>

            <div className="flex justify-end gap-3 items-center w-full md:invisible group-hover:visible">
                {prompt.isUserPrompt && (
                    <>
                        <Button
                            variant="secondaryRound"
                            onClick={() => handleDelete(prompt.identifier)}
                            tooltipText={i18n.translate({
                                id: 'tooltip.deleteAssistant',
                                sourceName: 'Assistants',
                            })}
                        >
                            <TrashIcon />
                        </Button>
                        <Button
                            variant="secondaryRound"
                            onClick={() => handleEdit(prompt.identifier)}
                            tooltipText={i18n.translate({
                                id: 'tooltip.editAssistant',
                                sourceName: 'Assistants',
                            })}
                        >
                            <PencilSimpleIcon />
                        </Button>
                        <Button
                            variant="secondaryRound"
                            onClick={() =>
                                prompt.proposedGlobal
                                    ? handleRemoveProposedGlobal(prompt.identifier)
                                    : handleProposeGlobal(prompt.identifier)
                            }
                            tooltipText={i18n.translate({
                                id: 'tooltip.' + (prompt.proposedGlobal ? 'unproposeGlobal' : 'proposeGlobal'),
                                sourceName: 'Assistants',
                            })}
                        >
                            {prompt.proposedGlobal ? <GlobeXIcon /> : <GlobeIcon />}
                        </Button>
                    </>
                )}

                {!prompt.isUserPrompt && (userSettings.isAdmin || userSettings.isSuperAdmin) && (
                    <Button
                        variant="secondaryRound"
                        onClick={() => navigate(`/admin/assistants/${prompt.identifier}`)}
                        tooltipText={i18n.translate({
                            id: 'tooltip.editAssistant',
                            sourceName: 'Assistants',
                        })}
                    >
                        <PencilSimpleIcon />
                    </Button>
                )}

                <Button
                    variant="secondaryRound"
                    onClick={() => createNewChat(prompt.identifier)}
                    tooltipText={i18n.translate({
                        id: 'tooltip.newChat',
                        sourceName: 'Assistants',
                    })}
                >
                    <NewChat className="inline" />
                </Button>
            </div>
        </AdminBox>
    );
}
